/**
 * featureFlags — minimal local registry + reactive hook.
 *
 * Phase 7 v149 — pre-launch infra. LaunchDarkly-style API surface but
 * backed by:
 *   1. compile-time defaults (FLAG_DEFAULTS map below)
 *   2. localStorage overrides (`storica_flag_*`) для QA / canary toggling
 *   3. future: profile.lab_flags from /athlete/profile (wired via
 *      setRemoteFlags() called from app shell on profile load)
 *
 * Goal: code can ask `useFeatureFlag('ai_insights_local_only')` today
 * без waiting for a remote service. When a real flag SDK ships, replace
 * the read() implementation; callers stay unchanged.
 */
import { useEffect, useState } from "react";

/**
 * Registered flags. Adding a flag here = one line; downstream callers
 * get type-safety и autocomplete. Values are the *default* state when
 * neither local override nor remote setting overrides.
 */
export const FLAG_DEFAULTS = {
  /** Use local rule-based AI insights instead of LLM (no API key yet). */
  ai_insights_local_only: true,
  /** Show experimental v5 chart canon (early-access opt-in). */
  experimental_charts: false,
  /** Render expanded methodology blocks in drill drawer. */
  verbose_drill_drawer: false,
  /** Surface admin-only beta features (Coach pool reorder, alerts CRUD v2). */
  admin_beta: false,
} as const;

export type FlagName = keyof typeof FLAG_DEFAULTS;

const STORAGE_PREFIX = "storica_flag_";

// Remote overrides set once from app shell after profile load.
let remoteFlags: Partial<Record<FlagName, boolean>> = {};

/**
 * setRemoteFlags — call from app shell when /athlete/profile responds
 * with a lab_flags object. Subsequent reads incorporate these values.
 */
export function setRemoteFlags(next: Partial<Record<FlagName, boolean>>): void {
  remoteFlags = next ?? {};
  // Fire a window event so live `useFeatureFlag()` callers update без
  // requiring a parent re-render.
  try {
    window.dispatchEvent(new CustomEvent("storica:flags-updated"));
  } catch {
    // silent: CustomEvent unavailable in SSR — flags will still apply on next read
  }
}

function readLocalOverride(name: FlagName): boolean | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + name);
    if (raw === "true") return true;
    if (raw === "false") return false;
    return null;
  } catch {
    // silent: localStorage unavailable
    return null;
  }
}

/**
 * read — synchronous precedence: localStorage override → remote → default.
 */
export function read(name: FlagName): boolean {
  const local = readLocalOverride(name);
  if (local !== null) return local;
  if (name in remoteFlags) return remoteFlags[name] ?? FLAG_DEFAULTS[name];
  return FLAG_DEFAULTS[name];
}

/**
 * setLocalOverride — QA / dev-tooling helper. Persists immediately.
 */
export function setLocalOverride(name: FlagName, value: boolean | null): void {
  try {
    if (value === null) {
      window.localStorage.removeItem(STORAGE_PREFIX + name);
    } else {
      window.localStorage.setItem(STORAGE_PREFIX + name, String(value));
    }
    window.dispatchEvent(new CustomEvent("storica:flags-updated"));
  } catch {
    // silent: localStorage unavailable
  }
}

/**
 * useFeatureFlag — reactive hook. Re-renders when remote flags arrive
 * or a local override changes (via `storica:flags-updated` event).
 */
export function useFeatureFlag(name: FlagName): boolean {
  const [value, setValue] = useState<boolean>(() => read(name));

  useEffect(() => {
    const onUpdate = () => setValue(read(name));
    window.addEventListener("storica:flags-updated", onUpdate);
    window.addEventListener("storage", onUpdate);
    return () => {
      window.removeEventListener("storica:flags-updated", onUpdate);
      window.removeEventListener("storage", onUpdate);
    };
  }, [name]);

  return value;
}
